import { type ReactNode, useEffect, useId, useState } from "react";
import { SiteLink } from "@/components/SiteLink";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/hooks/useLanguage";
import { onOpenCookieSettings, readConsent, saveConsent } from "@/lib/consent/store";
import { analyticsKeys } from "@/lib/tracking/events";

const COPY = {
  cs: {
    title: "Cookies",
    body: "S vaším souhlasem měříme návštěvnost (Google Analytics, PostHog). Částky, čísla účtů ani doklady nikdy neposíláme.",
    policy: "Zásady cookies",
    acceptAll: "Přijmout vše",
    rejectAll: "Odmítnout",
    settings: "Nastavení",
    dialogTitle: "Nastavení cookies",
    dialogBody:
      "Vyberte, které cookies smíme používat. Volbu si pamatujeme šest měsíců, pak se zeptáme znovu.",
    necessary: "Nezbytné",
    necessaryBody:
      "Přihlášení, zabezpečení, jazyk a tato volba. Bez nich web ani aplikace nefungují.",
    alwaysOn: "Vždy zapnuté",
    analytics: "Analytické",
    analyticsBody:
      "Google Analytics 4 (Google Ireland Limited) a PostHog (data v EU) měří navštívené stránky a vybrané akce v aplikaci. Cookies _ga, _ga_* a ph_*_posthog, platnost 6 měsíců.",
    save: "Uložit výběr",
    rejectAllLong: "Odmítnout vše",
  },
  en: {
    title: "Cookies",
    body: "With your consent we measure visits (Google Analytics, PostHog). We never send amounts, account numbers or documents.",
    policy: "Cookie policy",
    acceptAll: "Accept all",
    rejectAll: "Reject",
    settings: "Settings",
    dialogTitle: "Cookie settings",
    dialogBody:
      "Choose which cookies we may use. We remember your choice for six months, then ask again.",
    necessary: "Necessary",
    necessaryBody:
      "Sign-in, security, language and this choice. The website and the app do not work without them.",
    alwaysOn: "Always on",
    analytics: "Analytics",
    analyticsBody:
      "Google Analytics 4 (Google Ireland Limited) and PostHog (data in the EU) measure the pages visited and selected actions in the app. Cookies _ga, _ga_* and ph_*_posthog, kept for 6 months.",
    save: "Save choice",
    rejectAllLong: "Reject all",
  },
} as const;

/** The three choices share one look: refusing is as easy and as visible as accepting. */
const choiceClass = "w-full sm:w-auto sm:min-w-[9rem]";
/** The banner's choices: small, one row, equal widths. */
const bannerChoiceClass = "h-8 flex-1 px-2 text-[13px]";

/**
 * The cookie banner and its settings dialog, on the public site and in the
 * app. Shows only when this build has analytics configured and the visitor
 * has not chosen yet (or the choice expired or its version changed). Renders
 * nothing on the server and on the first client render, so prerendered pages
 * hydrate unchanged.
 */
export function CookieConsent({ force = false }: { force?: boolean }) {
  const { language } = useLanguage();
  const t = COPY[language === "en" ? "en" : "cs"];
  const [mounted, setMounted] = useState(false);
  const [askBanner, setAskBanner] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const titleId = useId();

  useEffect(() => {
    setMounted(true);
    const configured = force || analyticsKeys(import.meta.env) !== null;
    const stored = readConsent();
    setAnalytics(stored?.analytics ?? false);
    setAskBanner(configured && !stored);
    return onOpenCookieSettings(() => {
      setAnalytics(readConsent()?.analytics ?? false);
      setDialogOpen(true);
    });
  }, [force]);

  const choose = (value: boolean) => {
    saveConsent(value);
    setAnalytics(value);
    setAskBanner(false);
    setDialogOpen(false);
  };

  if (!mounted) return null;

  return (
    <>
      {askBanner && !dialogOpen && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:inset-x-auto sm:left-0 sm:p-4">
          <section
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            className="w-full rounded-xl border bg-card p-3.5 text-card-foreground shadow-lg sm:w-[23rem]"
          >
            <p className="text-[13px] leading-snug text-muted-foreground">
              <span id={titleId} className="font-semibold text-foreground">
                {t.title}
              </span>
              {" · "}
              {t.body}{" "}
              <SiteLink
                to="/cookies"
                className="text-foreground underline underline-offset-2 hover:no-underline"
              >
                {t.policy}
              </SiteLink>
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className={bannerChoiceClass}
                onClick={() => choose(false)}
              >
                {t.rejectAll}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={bannerChoiceClass}
                onClick={() => setDialogOpen(true)}
              >
                {t.settings}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={bannerChoiceClass}
                onClick={() => choose(true)}
              >
                {t.acceptAll}
              </Button>
            </div>
          </section>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.dialogTitle}</DialogTitle>
            <DialogDescription>{t.dialogBody}</DialogDescription>
          </DialogHeader>
          <div className="divide-y rounded-lg border">
            <CategoryRow
              title={t.necessary}
              body={t.necessaryBody}
              control={
                <span className="text-[13px] font-medium text-muted-foreground">{t.alwaysOn}</span>
              }
            />
            <CategoryRow
              title={t.analytics}
              body={t.analyticsBody}
              control={
                <Switch
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  aria-label={t.analytics}
                />
              }
            />
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:space-x-0">
            <Button variant="outline" className={choiceClass} onClick={() => choose(false)}>
              {t.rejectAllLong}
            </Button>
            <Button variant="outline" className={choiceClass} onClick={() => choose(analytics)}>
              {t.save}
            </Button>
            <Button variant="outline" className={choiceClass} onClick={() => choose(true)}>
              {t.acceptAll}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({
  title,
  body,
  control,
}: {
  title: string;
  body: string;
  control: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0 pt-0.5">{control}</div>
    </div>
  );
}
